import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { BsCashStack, BsClock } from 'react-icons/bs';
import { LuTicketPercent } from 'react-icons/lu';

import {
  BookingStatusCode,
  PaymentStatusCode,
  type BookingStatusCode as BookingStatusCodeType,
  type PaymentStatusCode as PaymentStatusCodeType,
} from '@/models';

interface BadgeProps {
  className?: string;
  text?: string;
}

export const CashWalletBadge: FC<BadgeProps> = ({ className, text }) => {
  const { t } = useTranslation();
  const label = text ?? t('package.cashwallet.tag');

  return (
    <div
      className={`bg-wallet-bg inline-flex h-6 w-fit items-center gap-1 rounded px-2 ${className ?? ''}`}
    >
      <div className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white">
        <BsCashStack className="text-accent text-xs" />
      </div>
      <span className="text-accent text-sm leading-5">{label}</span>
    </div>
  );
};

export const TimesBadge: FC<BadgeProps> = ({ className, text }) => {
  const { t } = useTranslation();
  const label = text ?? t('package.times.tag');

  return (
    <div
      className={`bg-times-bg inline-flex h-6 w-fit items-center justify-center gap-1 overflow-hidden rounded px-1 ${className ?? ''}`}
    >
      <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white p-[3px]">
        <img
          src="/icons/treatment_time.png"
          className="h-3 w-3 object-contain"
        />
      </span>
      <span className="text-times-text text-center text-sm leading-5 whitespace-nowrap">
        {label}
      </span>
    </div>
  );
};

export const MinutesBadge: FC<BadgeProps> = ({ className, text }) => {
  const { t } = useTranslation();
  const label = text ?? t('package.minutes.tag');

  return (
    <div
      className={`bg-minutes-bg inline-flex h-6 w-fit items-center rounded px-2 ${className ?? ''}`}
    >
      <span className="mr-1 inline-flex h-3 w-3 items-center justify-center rounded-full bg-white">
        <BsClock className="text-minutes-text h-2.5 w-2.5" />
      </span>
      <span className="text-minutes-text text-sm leading-5">{label}</span>
    </div>
  );
};

export const PercentBadge: FC<BadgeProps> = ({ className, text }) => {
  const { t } = useTranslation();
  const label = text ?? t('package.percent.tag');

  return (
    <div
      className={`bg-discount-bg inline-flex h-6 w-fit items-center gap-1 rounded px-2 ${className ?? ''}`}
    >
      <div className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white">
        <LuTicketPercent className="text-discount-text text-xs" />
      </div>
      <span className="text-discount-text text-sm leading-5">{label}</span>
    </div>
  );
};

type StatusTone = 'success' | 'warning' | 'error';

interface StatusBadgeProps {
  className?: string;
  label: string;
  tone: StatusTone;
}

const StatusBadge: FC<StatusBadgeProps> = ({ className, label, tone }) => {
  const toneClass =
    tone === 'success'
      ? 'bg-times-bg text-success'
      : tone === 'warning'
        ? 'bg-warning-bg text-warning'
        : 'bg-error/10 text-error';

  return (
    <div
      className={`${toneClass} inline-flex h-6 w-fit items-center justify-center overflow-hidden rounded px-2 ${className ?? ''}`}
    >
      <span className="text-sm leading-5 whitespace-nowrap">{label}</span>
    </div>
  );
};

interface BookingStatusBadgeProps {
  className?: string;
  status?: BookingStatusCodeType | null;
}

export const BookingStatusBadge: FC<BookingStatusBadgeProps> = ({
  className,
  status,
}) => {
  const { t } = useTranslation();

  if (status === BookingStatusCode.Confirmed) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.bookingStatus.confirmed')}
        tone="success"
      />
    );
  }

  if (status === BookingStatusCode.Canceled) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.bookingStatus.cancelled')}
        tone="error"
      />
    );
  }

  if (status === BookingStatusCode.Completed) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.bookingStatus.completed')}
        tone="success"
      />
    );
  }

  return null;
};

interface PaymentStatusBadgeProps {
  className?: string;
  status?: PaymentStatusCodeType | null;
}

export const PaymentStatusBadge: FC<PaymentStatusBadgeProps> = ({
  className,
  status,
}) => {
  const { t } = useTranslation();

  if (status === PaymentStatusCode.Paid) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.paymentStatus.paid')}
        tone="success"
      />
    );
  }

  if (status === PaymentStatusCode.PartiallyPaid) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.paymentStatus.partiallyPaid')}
        tone="warning"
      />
    );
  }

  if (status === PaymentStatusCode.Unpaid) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.paymentStatus.unpaid')}
        tone="error"
      />
    );
  }

  if (status === PaymentStatusCode.Refunded) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.paymentStatus.refunded')}
        tone="error"
      />
    );
  }

  if (status === PaymentStatusCode.PartiallyRefunded) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.paymentStatus.partiallyRefunded')}
        tone="error"
      />
    );
  }

  if (status === PaymentStatusCode.Canceled) {
    return (
      <StatusBadge
        className={className}
        label={t('profile.bookingDetails.paymentStatus.canceled')}
        tone="error"
      />
    );
  }

  return null;
};
